import { Box, Paper, Typography, CircularProgress, Button, useTheme } from '@mui/material'
import type { Message } from "@/pages/chat/types/chat"
import { formatDistanceToNow } from "date-fns"
import { CheckCircle } from '@mui/icons-material'
import { Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import Spread from '@/pages/Reading/Spread'
import DirectAnswerCard from '@/components/reading/DirectAnswerCard'
import CardInteractionsSection from '@/components/reading/CardInteractionsSection'
import PracticalAdviceSection from '@/components/reading/PracticalAdviceSection'
import ConclusionSection from '@/components/reading/ConclusionSection'
import ReadingLike from '@/components/reading/ReadingLike'
import { track } from '@/third-party/MixPanel'
import { useAuth } from '@/context/AuthContext'

interface MessageListProps {
    messages: Message[]
    isTyping?: boolean
}

export default function MessageList({ messages, isTyping = false }: MessageListProps) {
    const muiTheme = useTheme()
    const { t } = useTranslation()
    const { user } = useAuth()

    // State for managing reading evaluations
    const [evaluations, setEvaluations] = useState<{ [key: string]: boolean | null }>({})
    const [evaluationLoadingStates, setEvaluationLoadingStates] = useState<{ [key: string]: boolean }>({})

    const handleEvaluationClick = async (readingId: string, isHelpful: boolean) => {
        if (!readingId || !user || evaluationLoadingStates[readingId]) return

        track('spread_reading_evaluated', { isHelpful, readingId })
        setEvaluationLoadingStates(prev => ({ ...prev, [readingId]: true }))

        try {
            const token = localStorage.getItem('token')
            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/tarot/reading/${readingId}/like`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ helpful: isHelpful })
            })

            if (response.ok) {
                const data = await response.json()
                setEvaluations(prev => ({ ...prev, [readingId]: data.helpful }))
            } else {
                throw new Error('Failed to evaluate reading')
            }
        } catch (error) {
            console.error('Error evaluating reading:', error)
        } finally {
            setEvaluationLoadingStates(prev => ({ ...prev, [readingId]: false }))
        }
    }

    const finishCTA = () => {
        const timestamp = new Date()
        const formattedTime = formatDistanceToNow(timestamp, { addSuffix: true })
        const message = `Se quiser aprofundar essa leitura, você pode entrar na plataforma e explorar a versão completa da Luma: <a href="https://app.lumatarot.com" target="_blank" rel="noopener noreferrer" style="color: #8B5B52; text-decoration: underline;">https://app.lumatarot.com</a>.`
        return regularMessage({ content: message }, false, formattedTime, true)
    }

    const displaySpreadResult = (message: Message) => {
        // Extract reading data from message
        const readingData = message.spreadData

        if (!readingData || !readingData.cards) {
            return null
        }

        return (
            <Box
                key={message.id}
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    width: '100%',
                    flexDirection: 'column',
                    gap: 2
                }}
            >
                {/* Display the spread with selected cards */}
                <Box sx={{ width: '100%', mt: 2 }}>
                    <Spread
                        type={{ selectedReading: { type: readingData.spreadType || 'three-card' } }}
                        cards={readingData.cards}
                    />
                </Box>

                {/* Display the direct answer and practical advice */}
                <DirectAnswerCard
                    directAnswer={readingData.directAnswer}
                    finalConclusion={readingData.finalConclusion}
                    practicalAdvice={readingData.practicalAdvice || []}
                    showShareButton={false}
                />

                {/* Display card interactions if available */}
                <CardInteractionsSection
                    cardInteractions={readingData.cardInteractions || []}
                    cards={readingData.cards}
                />

                {/* Display practical advice if available */}
                <PracticalAdviceSection
                    practicalAdvice={readingData.practicalAdvice || []}
                />

                {/* Display conclusion if available */}
                <ConclusionSection
                    conclusion={readingData.finalConclusion}
                />

                {finishCTA()}

                {/* Display evaluation component if readingId is available */}
                {readingData.readingId && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                        <ReadingLike
                            readingId={readingData.readingId}
                            evaluation={evaluations[readingData.readingId] ?? null}
                            isLoading={evaluationLoadingStates[readingData.readingId] ?? false}
                            onEvaluationClick={(isHelpful) => handleEvaluationClick(readingData.readingId!, isHelpful)}
                        />
                    </Box>
                )}
            </Box>
        )
    }

    const regularMessage = (message: any, isUser: boolean, formattedTime: any, isHtml: boolean = false) => {
        return (
            <Box
                key={message.id}
                sx={{
                    display: 'flex',
                    justifyContent: isUser ? 'flex-end' : 'flex-start'
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        maxWidth: { xs: '80%', md: '70%' },
                        p: 2,
                        bgcolor: isUser ? 'rgba(139, 91, 82, 0.1)' : 'background.paper',
                        borderRadius: '12px',
                        border: '1px solid rgba(201, 173, 167, 0.4)',
                        ...(isUser ? {
                            borderTopRightRadius: 0,
                        } : {
                            borderTopLeftRadius: 0,
                        })
                    }}
                >
                    {isHtml ? (
                        <Typography
                            sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: 'text.primary',
                                fontSize: '1rem',
                                lineHeight: 1.5,
                                '& a': {
                                    color: '#8B5B52',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        color: '#6d4842'
                                    }
                                }
                            }}
                            dangerouslySetInnerHTML={{ __html: message.content }}
                        />
                    ) : (
                        <Typography
                            sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: 'text.primary',
                                fontSize: '1rem',
                                lineHeight: 1.5,
                                '& a': {
                                    color: '#8B5B52',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        color: '#6d4842'
                                    }
                                }
                            }}
                        >
                            {message.content}
                        </Typography>
                    )}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isUser ? 'flex-end' : 'flex-start',
                        mt: 1,
                        gap: 0.5
                    }}>
                        <Typography variant="caption" color="text.secondary">
                            {formattedTime}
                        </Typography>
                        {isUser && (
                            <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                        )}
                    </Box>
                </Paper>
            </Box>)
    }
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {messages.length === 0 ? (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '200px'
                }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            bgcolor: 'background.paper',
                            borderRadius: '12px',
                            border: '1px solid rgba(201, 173, 167, 0.4)'
                        }}
                    >
                        <Typography color="text.secondary">
                            Send a message to start chatting with the AI assistant
                        </Typography>
                    </Paper>
                </Box>
            ) : (
                <>
                    {messages.map((message) => {
                        const isUser = message.role === "user"
                        const timestamp = message.timestamp || new Date()
                        const formattedTime = formatDistanceToNow(timestamp, { addSuffix: true })
                        console.log(message.step)
                        // Render spread message differently
                        if (message.step === 2 && message.spreadData) {
                            return displaySpreadResult(message)
                        }
                        // Render spread message differently
                        if (message.type === "spread" && message.spreadData) {
                            return (
                                <Box
                                    key={message.id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        width: '100%'
                                    }}
                                >
                                    <Box sx={{ width: '100%', mt: 3 }}>
                                        <Spread
                                            type={{ selectedReading: { type: message.spreadData.spreadType } }}
                                            cards={message.spreadData.cards}
                                        />

                                        {message.spreadData.canChooseCards && message.spreadData.onChooseCards && (
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                mt: 3
                                            }}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={message.spreadData.onChooseCards}
                                                    sx={{
                                                        fontFamily: '"Playfair Display", serif',
                                                        fontSize: '0.85rem',
                                                        py: 1.5,
                                                        px: 3,
                                                        borderRadius: '12px',
                                                        textTransform: 'none',
                                                        width: "100%",
                                                        boxShadow: `0 4px 12px ${muiTheme.palette.primary.main}30`,
                                                        background: `linear-gradient(135deg, ${muiTheme.palette.primary.main} 0%, ${muiTheme.palette.primary.dark} 100%)`,
                                                        '&:hover': {
                                                            boxShadow: `0 6px 16px ${muiTheme.palette.primary.main}40`,
                                                            transform: 'translateY(-2px)',
                                                            transition: 'all 0.3s ease'
                                                        }
                                                    }}
                                                >
                                                    <Sparkles
                                                        className="animate-pulse"
                                                        style={{
                                                            width: '18px',
                                                            height: '18px',
                                                            marginRight: '8px',
                                                        }}
                                                    />
                                                    {message.spreadData.cards.length > 0 ? t('welcomeSpread.updateCards') || 'Update Cards' : t('welcomeSpread.chooseCards') || 'Choose Cards'}
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )
                        }

                        // Regular text message
                        return (regularMessage(message, isUser, formattedTime))
                    })}

                    {isTyping && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    bgcolor: 'background.paper',
                                    borderRadius: '12px',
                                    borderTopLeftRadius: 0,
                                    border: '1px solid rgba(201, 173, 167, 0.4)'
                                }}
                            >
                                <CircularProgress size={20} thickness={5} sx={{ color: '#8B5B52' }} />
                            </Paper>
                        </Box>
                    )}
                </>
            )}
        </Box>
    )
}

